<!-- eslint-disable -->
<template>
  <div class="qt-panel">
    <!-- Loading -->
    <div v-if="loading" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="sr-only">Loading…</span>
      </div>
    </div>

    <!-- No quotation yet -->
    <div v-else-if="!quotation" class="qt-empty">
      <feather-icon icon="FileTextIcon" size="30" class="text-muted mb-1" />
      <p class="text-muted mb-1">No quotation for this opportunity yet.</p>
      <label class="qt-check">
        <input v-model="prefill" type="checkbox" />
        <span>Prefill from the {{ card.serviceScheme || 'scheme' }} price list</span>
      </label>
      <button type="button" class="qt-btn qt-btn--primary mt-1" :disabled="creating" @click="createQuotation">
        <feather-icon icon="PlusIcon" size="14" />
        <span>{{ creating ? 'Creating…' : 'Create quotation' }}</span>
      </button>
    </div>

    <!-- Quotation -->
    <div v-else class="qt-doc">
      <header class="qt-head">
        <div>
          <span class="qt-code">{{ quotation.quotation_code }}</span>
          <span class="qt-status" :class="`qt-status--${quotation.status}`">{{ statusLabel(quotation.status) }}</span>
        </div>
        <div class="qt-total">
          {{ quotation.currency }} {{ money(quotation.total) }}
        </div>
      </header>

      <!--
        BRD §4.2 header fields. Request Number is the only REQUIRED one; it is the
        ERP request this quotation belongs to, and §3.1 gates the Proposal stage on
        it. It is not enforced when the quotation is created (the panel creates it
        with one click, before the rep has the number) — instead "Mark sent" stays
        disabled until it is filled, because that is the moment the document leaves
        the building.
      -->
      <div class="qt-fields">
        <label class="qt-field">
          <span>Request number *</span>
          <input v-model="requestNumber" type="text" class="qt-input qt-input--boxed" placeholder="ERP request no." />
        </label>
        <label class="qt-field">
          <span>Quotation date</span>
          <input v-model="quotationDate" type="date" class="qt-input qt-input--boxed" />
        </label>
        <label class="qt-field">
          <span>Expiration</span>
          <input v-model="expiration" type="date" class="qt-input qt-input--boxed" />
        </label>
        <label class="qt-field">
          <span>Payment terms</span>
          <input v-model="paymentTerms" type="text" class="qt-input qt-input--boxed" placeholder="e.g. 50% advance, 30 days" />
        </label>
        <!--
          BRD §4.2: "Broker Name (Visible only if 'Is broker?' is true)" — so this
          only appears when the deal says we are dealing with a broker. The flag
          lives on the card (crm_cards.is_broker) and is set on lead conversion.
        -->
        <label v-if="card.isBroker" class="qt-field qt-field--wide">
          <span>Broker name</span>
          <input v-model="brokerName" type="text" class="qt-input qt-input--boxed" placeholder="Who is paying / acting for the facility" />
        </label>
      </div>
      <p v-if="!requestNumber" class="qt-warn">
        <feather-icon icon="AlertCircleIcon" size="13" class="mr-05" />
        Add the request number before sending: it is what ties this quotation to the ERP request the operations team will work from.
      </p>
      <!--
        Karim, 3 Sep: "we follow this request and close the opportunity
        automatically... this is how CRM works." Once a request number is
        saved, crm:reconcile-quotation-requests (every 15 min, plus this
        panel's own check right after Save) watches for the ERP to issue the
        certificate and wins the opportunity by itself — no more coming back
        at the end to type a certificate number by hand.
      -->
      <p v-if="requestNumber && card.certificateStatus === 'matched'" class="qt-follow qt-follow--won">
        <feather-icon icon="CheckCircleIcon" size="13" class="mr-05" />
        Certificate {{ card.certificateNumber }} issued — this opportunity closed itself.
      </p>
      <p v-else-if="requestNumber" class="qt-follow">
        <feather-icon icon="RefreshCwIcon" size="13" class="mr-05" :class="{ spinning: checkingRequest }" />
        Following request {{ requestNumber }} — will close automatically once the ERP issues the certificate.
        <button type="button" class="qt-follow__check" :disabled="checkingRequest" @click="checkRequestNow">
          {{ checkingRequest ? 'Checking…' : 'Check now' }}
        </button>
      </p>

      <!-- Items -->
      <table class="qt-table">
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-center" style="width:60px">Qty</th>
            <th class="text-right" style="width:100px">Unit</th>
            <th class="text-right" style="width:100px">Total</th>
            <th style="width:30px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in items" :key="i">
            <td>
              <input v-model="item.description" type="text" class="qt-input" placeholder="Service / fee" />
              <small v-if="item.activity" class="text-muted">{{ item.activity }}</small>
            </td>
            <td><input v-model.number="item.qty" type="number" min="0" step="1" class="qt-input qt-input--num" /></td>
            <td><input v-model.number="item.unitPrice" type="number" min="0" step="0.01" class="qt-input qt-input--num text-right" /></td>
            <td class="text-right font-weight-bold">{{ money(rowTotal(item)) }}</td>
            <td>
              <button type="button" class="qt-icon-btn" title="Remove" @click="items.splice(i, 1)">
                <feather-icon icon="XIcon" size="13" />
              </button>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="5" class="text-center text-muted py-2">No line items yet.</td>
          </tr>
        </tbody>
      </table>

      <!-- Add row / from price list -->
      <div class="qt-actions-row">
        <button type="button" class="qt-btn qt-btn--ghost qt-btn--sm" @click="addBlankItem">
          <feather-icon icon="PlusIcon" size="13" /> <span>Add line</span>
        </button>
        <select class="qt-select qt-select--sm ml-1" @change="addFromPrice($event)">
          <option value="">+ From price list…</option>
          <option v-for="p in prices" :key="p.id" :value="p.id">
            {{ p.activity || p.name }} — {{ p.currency || '' }} {{ p.amount }}
          </option>
        </select>
      </div>

      <!-- Totals -->
      <div class="qt-totals">
        <div><span>Subtotal</span><strong>{{ money(subtotal) }}</strong></div>
        <div>
          <span>Discount<small class="text-muted"> (amount)</small></span>
          <input v-model.number="discount" type="number" min="0" step="0.01" class="qt-input qt-input--num text-right" />
        </div>
        <!-- BRD §4.1: over 15% this needs manager approval (workflow not built yet —
             the warning is here so nobody sends one unknowingly). -->
        <div v-if="discountPercent > 15" class="qt-totals__warn">
          <span>⚠ {{ discountPercent }}% discount</span>
          <strong>needs manager approval</strong>
        </div>
        <!-- Editable rate (Karim, 14 Aug): KSA 15%, UAE 5%, zero-rated services 0. -->
        <div>
          <span>
            VAT
            <input v-model.number="vatPercent" type="number" min="0" max="100" step="0.5" class="qt-input qt-input--pct" />%
          </span>
          <strong>{{ money(vatAmount) }}</strong>
        </div>
        <div class="qt-totals__grand"><span>Total</span><strong>{{ quotation.currency }} {{ money(grandTotal) }}</strong></div>
      </div>

      <textarea v-model="notes" rows="2" class="qt-textarea mt-1" placeholder="Notes for the client…"></textarea>

      <!-- Buttons -->
      <div class="qt-footer">
        <button type="button" class="qt-btn qt-btn--secondary qt-btn--sm" :disabled="saving" @click="saveQuotation">
          <feather-icon icon="SaveIcon" size="13" /> <span>{{ saving ? 'Saving…' : 'Save' }}</span>
        </button>

        <button
          v-if="quotation.status === 'draft'"
          type="button" class="qt-btn qt-btn--primary qt-btn--sm"
          :disabled="!requestNumber"
          :title="requestNumber ? 'Mark this quotation as sent to the client' : 'Fill the request number first (BRD: required on every quotation)'"
          @click="setStatus('sent')"
        >
          <feather-icon icon="SendIcon" size="13" /> <span>Mark sent</span>
        </button>

        <button
          v-if="quotation.status === 'sent'"
          type="button" class="qt-btn qt-btn--success qt-btn--sm"
          @click="setStatus('accepted')"
        >
          <feather-icon icon="CheckIcon" size="13" /> <span>Accepted</span>
        </button>

        <button
          v-if="quotation.status === 'sent'"
          type="button" class="qt-btn qt-btn--danger qt-btn--sm"
          @click="setStatus('rejected')"
        >
          <feather-icon icon="XIcon" size="13" /> <span>Rejected</span>
        </button>

        <button
          v-if="quotation.status === 'accepted'"
          type="button" class="qt-btn qt-btn--success qt-btn--sm"
          title="Sales is done — pass to the operation team"
          @click="setStatus('handed_over')"
        >
          <feather-icon icon="ArrowRightCircleIcon" size="13" /> <span>Hand over to operations</span>
        </button>
      </div>

      <p v-if="quotation.status === 'handed_over'" class="qt-note">
        <feather-icon icon="CheckCircleIcon" size="13" class="mr-05" />
        Handed over to operations — they now create the ERP request &amp; certificates.
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'QuotationPanel',
  props: {
    card: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      creating: false,
      saving: false,
      checkingRequest: false,
      prefill: true,
      quotation: null,
      items: [],
      prices: [],
      discount: 0,
      // Editable VAT rate for THIS quotation (KSA 15, UAE 5, zero-rated 0).
      vatPercent: 15,
      notes: '',
      // BRD §4.2 header fields (ledger G-13). Kept as their own state, like
      // discount/notes, so Save posts them together with the line items.
      requestNumber: '',
      quotationDate: '',
      expiration: '',
      paymentTerms: '',
      brokerName: ''
    }
  },
  computed: {
    subtotal() {
      return this.items.reduce((sum, i) => sum + this.rowTotal(i), 0)
    },
    /** Net of discount, before VAT. */
    netTotal() {
      return Math.max(0, this.subtotal - (Number(this.discount) || 0))
    },
    /**
     * (Subtotal − discount) + VAT at the rate on THIS quotation.
     * Mirrors CrmQuotation::recalcTotals() — if you change one, change the other.
     */
    vatAmount() {
      const rate = (Number(this.vatPercent) || 0) / 100
      return Math.round(this.netTotal * rate * 100) / 100
    },
    grandTotal() {
      return Math.round((this.netTotal + this.vatAmount) * 100) / 100
    },
    /** BRD §4.1 — a discount over 15% needs manager approval. */
    discountPercent() {
      if (!this.subtotal) return 0
      return Math.round(((Number(this.discount) || 0) / this.subtotal) * 1000) / 10
    }
  },
  watch: {
    'card.id': { handler: 'fetchQuotation', immediate: true }
  },
  methods: {
    money(v) {
      const n = Number(v) || 0
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    rowTotal(item) {
      return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0)
    },
    statusLabel(s) {
      const map = {
        draft: 'Draft', sent: 'Sent', accepted: 'Accepted',
        rejected: 'Rejected', handed_over: 'Handed over'
      }
      return map[s] || s
    },
    notify(title, text, variant = 'success', icon = 'CheckIcon') {
      this.$toast({ component: ToastificationContent, props: { title, text, variant, icon } })
    },

    async fetchQuotation() {
      if (!this.card || !this.card.id) return
      this.loading = true
      this.quotation = null
      try {
        const res = await axios.get('crm/quotations', { params: { card_id: this.card.id } })
        const body = decryptData(res.data.payload)
        const list = (body && body.data) || []
        if (list.length) {
          this.applyQuotation(list[0])
          this.fetchPrices()
        }
      } catch (e) {
        // no quotation yet
      } finally {
        this.loading = false
      }
    },

    applyQuotation(q) {
      this.quotation = q
      this.discount = Number(q.discount) || 0
      // null = nobody set a rate on this quotation; fall back to the KSA default,
      // but an explicit 0 (zero-rated) must survive.
      this.vatPercent = (q.vat_percent === null || q.vat_percent === undefined)
        ? 15
        : Number(q.vat_percent)
      this.notes = q.notes || ''
      // BRD §4.2 header fields. quotation_date / expiration arrive as Y-m-d
      // (CrmQuotation casts them as 'date:Y-m-d'), which is what <input
      // type="date"> needs — no reformatting here.
      this.requestNumber = q.request_number || ''
      this.quotationDate = q.quotation_date || ''
      this.expiration = q.expiration || ''
      this.paymentTerms = q.payment_terms || ''
      this.brokerName = q.broker_name || ''
      this.items = (q.items || []).map(i => ({
        description: i.description,
        activity: i.activity,
        scheme: i.scheme,
        priceId: i.price_id,
        qty: Number(i.qty) || 1,
        unitPrice: Number(i.unit_price) || 0,
        currency: i.currency
      }))
    },

    async fetchPrices() {
      try {
        const params = {}
        if (this.card.serviceScheme) params.scheme = this.card.serviceScheme
        const res = await axios.get('crm/service-prices', { params })
        const body = decryptData(res.data.payload)
        this.prices = (body && body.data) || []
        if (!this.prices.length) {
          const all = await axios.get('crm/service-prices')
          const b2 = decryptData(all.data.payload)
          this.prices = ((b2 && b2.data) || []).slice(0, 100)
        }
      } catch (e) { /* optional */ }
    },

    async createQuotation() {
      this.creating = true
      try {
        const res = await axios.post('crm/quotations', {
          cardId: this.card.id,
          scheme: this.card.serviceScheme,
          companyName: this.card.title,
          contactPerson: this.card.client,
          currency: this.card.currency || 'AED',
          prefill: this.prefill
        })
        const body = decryptData(res.data.payload)
        this.applyQuotation(body.data)
        this.fetchPrices()
        this.notify('Quotation created', body.data.quotation_code)
        this.$emit('changed')
      } catch (e) {
        this.notify('Failed', 'Could not create the quotation', 'danger', 'AlertCircleIcon')
      } finally {
        this.creating = false
      }
    },

    addBlankItem() {
      this.items.push({ description: '', qty: 1, unitPrice: 0, currency: this.quotation.currency })
    },

    addFromPrice(ev) {
      const id = ev.target.value
      ev.target.value = ''
      if (!id) return
      const p = this.prices.find(x => String(x.id) === String(id))
      if (!p) return
      this.items.push({
        description: p.activity || p.name || 'Service',
        activity: p.activity,
        scheme: p.scheme,
        priceId: p.id,
        qty: 1,
        unitPrice: Number(p.amount) || 0,
        currency: p.currency || this.quotation.currency
      })
    },

    async saveQuotation() {
      this.saving = true
      try {
        const res = await axios.put(`crm/quotations/${this.quotation.id}`, {
          discount: this.discount,
          vat_percent: this.vatPercent,
          notes: this.notes,
          // snake_case on purpose: the controller's ->only() fill accepts these
          // keys AND accepts null through them, so clearing a date really clears
          // it (the camelCase aliases there use isset() and cannot clear).
          request_number: this.requestNumber || null,
          quotation_date: this.quotationDate || null,
          expiration: this.expiration || null,
          payment_terms: this.paymentTerms || null,
          broker_name: this.brokerName || null,
          items: this.items
        })
        const body = decryptData(res.data.payload)
        this.applyQuotation(body.data)
        this.notify('Quotation saved', `${this.quotation.currency} ${this.money(this.quotation.total)}`)
        this.$emit('changed')
        // Karim, 3 Sep: the moment a request number is saved, check right away
        // instead of making the rep wait for the next scheduled run — the
        // certificate may already exist (a renewal saved after the fact, or
        // simply a slow rep catching up on paperwork).
        if (this.requestNumber) this.checkRequestNow(true)
      } catch (e) {
        this.notify('Save failed', 'Could not save the quotation', 'danger', 'AlertCircleIcon')
      } finally {
        this.saving = false
      }
    },

    /** Karim, 3 Sep: "we follow this request and close the opportunity
     * automatically." Manual "check now", also fired right after Save. */
    async checkRequestNow(silent = false) {
      this.checkingRequest = true
      try {
        const res = await axios.post('crm/quotations/reconcile')
        const body = decryptData(res.data.payload)
        const autoWon = body?.data?.autoWon ?? 0
        if (autoWon > 0) {
          this.notify('Opportunity closed', 'The ERP has issued the certificate — this opportunity is now Won.')
          this.$emit('changed')
        } else if (!silent) {
          this.notify('Not issued yet', 'No certificate found for this request yet — still in progress.', 'warning', 'ClockIcon')
        }
      } catch (e) {
        if (!silent) this.notify('Check failed', 'Could not check the request', 'danger', 'AlertCircleIcon')
      } finally {
        this.checkingRequest = false
      }
    },

    async setStatus(status) {
      try {
        const res = await axios.post(`crm/quotations/${this.quotation.id}/status`, { status })
        const body = decryptData(res.data.payload)
        this.applyQuotation(body.data)
        this.notify('Quotation updated', this.statusLabel(status))
        this.$emit('changed')
      } catch (e) {
        this.notify('Failed', 'Could not update the status', 'danger', 'AlertCircleIcon')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.qt-panel { font-size: .82rem; }

.qt-empty { text-align: center; padding: 1.2rem .5rem; }
.qt-check { display: block; font-size: .76rem; color: #6e6b7b; margin-top: .3rem; cursor: pointer; }

.qt-head {
  display: flex; justify-content: space-between; align-items: center;
  padding-bottom: .5rem; border-bottom: 1px solid #f0f0f5; margin-bottom: .6rem;
}
.qt-code { font-family: monospace; font-weight: 700; font-size: .8rem; margin-right: .4rem; }
.qt-total { font-weight: 700; font-size: .95rem; color: #7367f0; }

.qt-status {
  font-size: .64rem; font-weight: 700; padding: .1rem .45rem; border-radius: 10px; text-transform: uppercase;
  &--draft { background: #f1f1f6; color: #6e6b7b; }
  &--sent { background: rgba(0,207,232,.14); color: #00a5b5; }
  &--accepted { background: rgba(40,199,111,.14); color: #28c76f; }
  &--rejected { background: rgba(234,84,85,.14); color: #ea5455; }
  &--handed_over { background: rgba(115,103,240,.14); color: #7367f0; }
}

.qt-table {
  width: 100%; border-collapse: collapse; font-size: .76rem;
  th { text-align: left; font-size: .66rem; text-transform: uppercase; color: #6e6b7b; padding: .25rem .3rem; border-bottom: 1px solid #f0f0f5; }
  td { padding: .25rem .3rem; border-bottom: 1px solid #f7f7fa; vertical-align: middle; }
}
.qt-input {
  width: 100%; border: 1px solid transparent; border-radius: .25rem; padding: .2rem .3rem;
  font-size: .76rem; background: transparent;
  &:hover { border-color: #e4e4ec; }
  &:focus { outline: none; border-color: #7367f0; background: #fff; }
  &--num { width: 100%; }
}
.qt-icon-btn { border: 0; background: transparent; color: #b9b9c3; cursor: pointer; &:hover { color: #ea5455; } }

/* BRD §4.2 header fields */
.qt-fields {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: .35rem .5rem; margin-bottom: .6rem;
}
.qt-field {
  display: flex; flex-direction: column; margin: 0;
  > span { font-size: .64rem; text-transform: uppercase; color: #6e6b7b; letter-spacing: .3px; margin-bottom: .1rem; }
  &--wide { grid-column: 1 / -1; }
}
.qt-input--boxed { border-color: #e4e4ec; background: #fff; }
/* VAT rate sits inline with its label, so it must not stretch like the others. */
.qt-input--pct { width: 52px !important; text-align: right; border-color: #e4e4ec; }
.qt-warn {
  display: flex; align-items: center; margin: -.2rem 0 .5rem; font-size: .72rem;
  color: #ff9f43; background: rgba(255,159,67,.1); border-radius: .3rem; padding: .3rem .45rem;
}

.qt-follow {
  display: flex; align-items: center; gap: .35rem; margin: -.2rem 0 .5rem; font-size: .72rem;
  color: #00b8d4; background: rgba(0,184,212,.1); border-radius: .3rem; padding: .3rem .45rem;

  &--won {
    color: #28c76f; background: rgba(40,199,111,.1); font-weight: 600;
  }

  .spinning { animation: qt-spin 1s linear infinite; }
}
.qt-follow__check {
  margin-left: auto; border: none; background: none; color: inherit; font-weight: 700;
  text-decoration: underline; font-size: .72rem; cursor: pointer;

  &:disabled { opacity: .6; cursor: default; }
}
@keyframes qt-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.qt-actions-row { display: flex; align-items: center; margin-top: .4rem; }
.qt-select {
  border: 1px solid #e4e4ec; border-radius: .25rem; padding: .2rem .3rem; font-size: .74rem;
  background: #fff; color: #5e5873; max-width: 190px;
}

.qt-totals {
  margin-top: .6rem; border-top: 1px solid #f0f0f5; padding-top: .4rem;
  > div { display: flex; justify-content: space-between; align-items: center; padding: .12rem 0; font-size: .78rem; color: #6e6b7b; }
  input { max-width: 90px; }
  &__grand { border-top: 1px dashed #e4e4ec; margin-top: .25rem; padding-top: .3rem !important; font-size: .88rem !important; color: #5e5873 !important; }
  &__warn { color: #b26a1b !important; font-weight: 700; }
}

.qt-textarea {
  width: 100%; border: 1px solid #e4e4ec; border-radius: .3rem; padding: .35rem .5rem; font-size: .76rem; resize: vertical;
  &:focus { outline: none; border-color: #7367f0; }
}

.qt-footer { display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .6rem; }

.qt-btn {
  display: inline-flex; align-items: center; gap: .3rem; border: 0; cursor: pointer;
  padding: .35rem .65rem; border-radius: .3rem; font-size: .76rem; font-weight: 600;
  background: #f1f1f4; color: #5e5873;
  &:hover { filter: brightness(.96); }
  &:disabled { opacity: .6; cursor: not-allowed; }
  &--primary { background: #7367f0; color: #fff; }
  &--secondary { background: #ededf3; color: #5e5873; }
  &--success { background: #28c76f; color: #fff; }
  &--danger { background: #ea5455; color: #fff; }
  &--ghost { background: #fff; border: 1px solid #e4e4ec; color: #5e5873; }
  &--sm { padding: .28rem .55rem; font-size: .73rem; }
}

.qt-note {
  margin-top: .5rem; font-size: .74rem; color: #28c76f; display: flex; align-items: center;
  background: rgba(40,199,111,.1); border-radius: .3rem; padding: .3rem .45rem;
}
.mr-05 { margin-right: .25rem; }
.ml-1 { margin-left: .35rem; }
</style>
